import type * as ents from '@attain-apps/entities'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import type * as router from '@attain-apps/fe/web/main/lib/router'
import * as store from '@attain-apps/fe/web/main/lib/store'

import { RouteParam } from '../../../route-param'
import type * as apps from '../apps'
import type * as tenants from '../tenants'
import type * as zones from '../zones'

import type { Feature } from './feature'

/**
 * Derives Admin navigation context and URLs from the active router state.
 */
export class Selectors {
  /**
   * Lazily instantiated selector for {@link ents.core.admin.Context.ForTenant}.
   *
   * Admin page context is owned by the URL. Entity collection current IDs can
   * represent modal edit focus, so they must not determine the active page
   * context.
   */
  public get tenantContext(): store.selectors.BoundSelector<
    ents.core.admin.Context.ForTenant | undefined
  > {
    return (this._tenantContext ??= this._router.selectors
      .getRouteParam(RouteParam.TenantId)
      .project(tenantId =>
        tenantId === undefined
          ? undefined
          : {
              tenantId,
            },
      ))
  }

  /**
   * Cached value for {@link tenantContext}
   */
  private _tenantContext:
    | store.selectors.BoundSelector<
        ents.core.admin.Context.ForTenant | undefined
      >
    | undefined

  /**
   * Lazily instantiated selector for {@link ents.core.admin.Context.ForZone}
   */
  public get zoneContext(): store.selectors.BoundSelector<
    ents.core.admin.Context.ForZone | undefined
  > {
    return (this._zoneContext ??= this.tenantContext.project(
      this._router.selectors.getRouteParam(RouteParam.ZoneId).asSelector,
      (tenantContext, zoneId) =>
        tenantContext === undefined || zoneId === undefined
          ? undefined
          : { ...tenantContext, zoneId },
    ))
  }
  /**
   * Cached value for {@link zoneContext}
   */
  private _zoneContext:
    | store.selectors.BoundSelector<ents.core.admin.Context.ForZone | undefined>
    | undefined

  /**
   * Lazily instantiated selector for {@link ents.core.admin.Context.ForApp}
   */
  public get appContext(): store.selectors.BoundSelector<
    ents.core.admin.Context.ForApp | undefined
  > {
    return (this._appContext ??= this.zoneContext.project(
      this._router.selectors.getRouteParam(RouteParam.AppId).asSelector,
      (zoneContext, appId) =>
        zoneContext === undefined || appId === undefined
          ? undefined
          : { ...zoneContext, appId },
    ))
  }
  /**
   * Cached value for {@link appContext}
   */
  private _appContext:
    | store.selectors.BoundSelector<ents.core.admin.Context.ForApp | undefined>
    | undefined

  /**
   * Lazily instantiated selector for {@link ents.core.admin.Context.ForScenario}
   */
  public get scenarioContext(): store.selectors.BoundSelector<
    ents.core.admin.Context.ForScenario | undefined
  > {
    return (this._scenarioContext ??= this.appContext.project(
      this._router.selectors.getRouteParam(RouteParam.ScenarioId).asSelector,
      (appContext, scenarioId) =>
        appContext === undefined || scenarioId === undefined
          ? undefined
          : { ...appContext, scenarioId },
    ))
  }
  /**
   * Cached value for {@link scenarioContext}
   */
  private _scenarioContext:
    | store.selectors.BoundSelector<
        ents.core.admin.Context.ForScenario | undefined
      >
    | undefined

  /**
   * Lazily instantiated selector for resolving the most specific available
   * {@link ents.core.admin.Context}.
   *
   * Context resolution priority:
   * 1. 'scenarioContext' — if available
   * 2. 'appContext' — if 'scenarioContext' is unavailable
   * 3. 'zoneContext' — if 'appContext' is unavailable
   * 4. 'tenantContext' — if both 'appContext' and 'zoneContext' are unavailable
   * 5. Returns undefined if none are available
   */
  public get activeEntityContext(): store.selectors.BoundSelector<
    ents.core.admin.Context | undefined
  > {
    return (this._activeEntityContext ??= this.tenantContext.project(
      this.zoneContext.asSelector,
      this.appContext.asSelector,
      this.scenarioContext.asSelector,
      (tenantContext, zoneContext, appContext, scenarioContext) =>
        scenarioContext ??
        appContext ??
        zoneContext ??
        tenantContext ??
        undefined,
    ))
  }
  /**
   * Cached value for {@link activeEntityContext}
   */
  private _activeEntityContext:
    | store.selectors.BoundSelector<ents.core.admin.Context | undefined>
    | undefined

  /**
   * Lazily resolves and caches the parent context for user assignment.
   *
   * The selected entity is assigned from users already present in its parent
   * scope, except tenant-level assignment, which uses the selected tenant.
   */
  public get contextForUserAssignment(): store.selectors.BoundSelector<
    | ents.core.admin.Context.ForTenant
    | ents.core.admin.Context.ForZone
    | ents.core.admin.Context.ForApp
    | undefined
  > {
    return (this._contextForUserAssignment ??= this.tenantContext.project(
      this.zoneContext.asSelector,
      this.appContext.asSelector,
      this.scenarioContext.asSelector,
      (tenantContext, zoneContext, appContext, scenarioContext) =>
        scenarioContext
          ? appContext
          : appContext
            ? zoneContext
            : zoneContext
              ? tenantContext
              : tenantContext,
    ))
  }
  /**
   * Cached value for {@link contextForUserAssignment}
   */
  private _contextForUserAssignment:
    | store.selectors.BoundSelector<
        | ents.core.admin.Context.ForTenant
        | ents.core.admin.Context.ForZone
        | ents.core.admin.Context.ForApp
        | undefined
      >
    | undefined

  /**
   * Lazily resolves and caches the context used for agent assignment.
   *
   * Agent assignment is only supported from tenant, zone, and app pages and
   * uses the selected entity itself as the candidate scope.
   */
  public get contextForAgentAssignment(): store.selectors.BoundSelector<
    | ents.core.admin.Context.ForTenant
    | ents.core.admin.Context.ForZone
    | ents.core.admin.Context.ForApp
    | undefined
  > {
    return (this._contextForAgentAssignment ??= this.tenantContext.project(
      this.zoneContext.asSelector,
      this.appContext.asSelector,
      (tenantContext, zoneContext, appContext) =>
        appContext ?? zoneContext ?? tenantContext,
    ))
  }
  /**
   * Cached value for {@link contextForAgentAssignment}
   */
  private _contextForAgentAssignment:
    | store.selectors.BoundSelector<
        | ents.core.admin.Context.ForTenant
        | ents.core.admin.Context.ForZone
        | ents.core.admin.Context.ForApp
        | undefined
      >
    | undefined

  /**
   * Constructs a URL that will navigate to the tenant list
   */
  public getUrlForTenants

  /**
   * Constructs a URL that will navigate to knowledge-base management.
   */
  public getUrlForKnowledgeBases

  /**
   * Constructs a URL that will navigate to the user list
   */
  public getUrlForUsers

  /**
   * Constructs a URL that will navigate to the desired tenant
   */
  public getUrlForTenant

  /**
   * Constructs a URL that will navigate to the desired zone
   */
  public getUrlForZone

  /**
   * Constructs a URL that will navigate to the desired app
   */
  public getUrlForApp

  /**
   * Constructs a URL that will navigate to the desired scenario
   */
  public getUrlForScenario

  /**
   * Resolves the currently active AdminTenant collection.
   */
  private get _tenantEc() {
    return ec.Collection.instances.get<tenants.TypeParams>(
      ec.Name.Predefined.AdminTenant,
    ).collection
  }

  /**
   * Resolves the currently active AdminZone collection.
   */
  private get _zoneEc() {
    return ec.Collection.instances.get<zones.TypeParams>(
      ec.Name.Predefined.AdminZone,
    ).collection
  }

  /**
   * Resolves the currently active AdminApp collection.
   */
  private get _appEc() {
    return ec.Collection.instances.get<apps.TypeParams>(
      ec.Name.Predefined.AdminApp,
    ).collection
  }

  /**
   * Indicates the user is currently on the tenants page
   */
  public readonly isCurrentlyOnTenants = this._router.selectors.rawUrl.project(
    rawUrl => rawUrl === '/admin/tenants',
  )
  /** Indicates the user is currently on the knowledge-base management page. */
  public readonly isCurrentlyOnKnowledgeBases =
    this._router.selectors.rawUrl.project(
      rawUrl => rawUrl === '/admin/knowledge-bases',
    )
  /**
   * Indicates the user is currently on the users page
   */
  public readonly isCurrentlyOnUsers = this._router.selectors.rawUrl.project(
    rawUrl => rawUrl === '/admin/users',
  )

  constructor(
    feature: Feature,
    private readonly _router: router.Router,
  ) {
    /**
     * @see {@link getUrlForTenants}
     */
    this.getUrlForTenants = feature.createSelectorMethod(() =>
      store.selectors.Selector.always(
        this._router.ngRouter.createUrlTree(['/admin/tenants']),
      ),
    )

    /**
     * @see {@link getUrlForKnowledgeBases}
     */
    this.getUrlForKnowledgeBases = feature.createSelectorMethod(() =>
      store.selectors.Selector.always(
        this._router.ngRouter.createUrlTree(['/admin/knowledge-bases']),
      ),
    )

    /**
     * @see {@link getUrlForUsers}
     */
    this.getUrlForUsers = feature.createSelectorMethod(() =>
      store.selectors.Selector.always(
        this._router.ngRouter.createUrlTree(['/admin/users']),
      ),
    )

    /**
     * @see {@link getUrlForTenant}
     */
    this.getUrlForTenant = feature.createSelectorMethod(
      ({ alias, id }: ents.core.common.FlexibleIdentifier<string>) => {
        if (id) {
          return store.selectors.Selector.always(
            this._router.ngRouter.createUrlTree(['/admin/tenants', id]),
          )
        }

        return store.selectors.Selector.create.usingGlobalState(state => {
          const tenant = Object.values(
            this._tenantEc.plugins.cachePlugin.feature.selectors.lookup.asSelector(
              state,
            ),
          ).find(_tenant => _tenant?.alias === alias)

          if (!tenant) {
            // If we couldn't find the tenant, we can't generate a URL
            return undefined
          }

          return this._router.ngRouter.createUrlTree([
            '/admin/tenants',
            tenant.id,
          ])
        })
      },
    )

    /**
     * @see {@link getUrlForZone}
     */
    this.getUrlForZone = feature.createSelectorMethod(
      ({ alias, id }: ents.core.common.FlexibleIdentifier<string>) => {
        if (id) {
          return this._router.selectors
            .getRouteParam(RouteParam.TenantId)
            .project(tenantId =>
              tenantId === undefined
                ? undefined
                : this._router.ngRouter.createUrlTree([
                    '/admin/tenants',
                    tenantId,
                    id,
                  ]),
            )
        }

        return store.selectors.Selector.create.usingGlobalState(state => {
          const zone = Object.values(
            this._zoneEc.plugins.cachePlugin.feature.selectors.lookup.asSelector(
              state,
            ),
          ).find(_zone => _zone?.alias === alias)
          const tenantId = this._router.selectors
            .getRouteParam(RouteParam.TenantId)
            .asSelector(state)

          if (!zone || tenantId === undefined) {
            // If we couldn't find the zone or current tenant id, we cannot
            // generate a URL
            return undefined
          }

          return this._router.ngRouter.createUrlTree([
            '/admin/tenants',
            tenantId,
            zone.id,
          ])
        })
      },
    )

    /**
     * @see {@link getUrlForApp}
     */
    this.getUrlForApp = feature.createSelectorMethod(
      ({ alias, id }: ents.core.common.FlexibleIdentifier<string>) => {
        if (id) {
          return this._router.selectors
            .getRouteParam(RouteParam.TenantId)
            .project(
              this._router.selectors.getRouteParam(RouteParam.ZoneId)
                .asSelector,
              (tenantId, zoneId) =>
                tenantId === undefined || zoneId === undefined
                  ? undefined
                  : this._router.ngRouter.createUrlTree([
                      '/admin/tenants',
                      tenantId,
                      zoneId,
                      id,
                    ]),
            )
        }

        return store.selectors.Selector.create.usingGlobalState(state => {
          const app = Object.values(
            this._appEc.plugins.cachePlugin.feature.selectors.lookup.asSelector(
              state,
            ),
          ).find(_app => _app?.alias === alias)
          const tenantId = this._router.selectors
            .getRouteParam(RouteParam.TenantId)
            .asSelector(state)
          const zoneId = this._router.selectors
            .getRouteParam(RouteParam.ZoneId)
            .asSelector(state)

          if (!app || tenantId === undefined || zoneId === undefined) {
            // If we couldn't find the app or current tenant or zone id, we
            // cannot generate a URL
            return undefined
          }

          return this._router.ngRouter.createUrlTree([
            '/admin/tenants',
            tenantId,
            zoneId,
            app.id,
          ])
        })
      },
    )

    /**
     * @see {@link getUrlForScenario}
     */
    this.getUrlForScenario = feature.createSelectorMethod(
      ({ id }: ents.core.common.FlexibleIdentifier<string>) => {
        if (!id) {
          return store.selectors.Selector.always(undefined)
        }

        return this.appContext.project(appContext => {
          if (!appContext) {
            // If we couldn't find the current app context, we cannot generate
            // a scenario user-assignment URL.
            return undefined
          }

          return this._router.ngRouter.createUrlTree([
            '/admin/tenants',
            appContext.tenantId,
            appContext.zoneId,
            appContext.appId,
            id,
          ])
        })
      },
    )
  }
}
